// IncomingConnectionsList.jsx - Componente per gestire l'ordine delle connessioni in ingresso
import React, { useState, useCallback } from 'react';
import './IncomingConnectionsList.css';

const IncomingConnectionsList = ({ node, connections, nodes, onUpdateConnectionOrder, onUpdateConnectionNumber, onDeleteConnection }) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Funzione per ottenere le connessioni in ingresso di un nodo
  const getIncomingConnections = useCallback(() => {
    const incomingConns = connections.filter(conn => conn.toId === node.id);
    
    // Ordina le connessioni basandosi su incomingConnectionsOrder
    const orderMap = node.incomingConnectionsOrder || {};
    
    return incomingConns
      .map(conn => {
        const sourceNode = nodes.find(n => n.id === conn.fromId);
        return {
          ...conn,
          sourceNodeName: sourceNode ? sourceNode.name : 'Nodo Sconosciuto',
          order: orderMap[conn.id] || 0,
          isReorderable: conn.type === 'GironeToDiretta' || conn.type === 'ClassificaToDiretta'
        };
      })
      .sort((a, b) => a.order - b.order);
  }, [connections, nodes, node]);

  const incomingConnections = getIncomingConnections();

  const handleDragStart = (e, index) => {
    const connection = incomingConnections[index];
    // Permetti il drag solo per connessioni riordinabili
    if (!connection.isReorderable) {
      e.preventDefault();
      return;
    }
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    // Permetti il drop su qualsiasi posizione se stiamo trascinando una connessione riordinabile
    if (draggedItem !== null && incomingConnections[draggedItem]?.isReorderable) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    // Verifica che stiamo trascinando una connessione riordinabile
    const draggedConnection = incomingConnections[draggedItem];
    if (!draggedConnection.isReorderable) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    // Crea una nuova lista con l'elemento spostato
    const newConnections = [...incomingConnections];
    
    // Rimuovi l'elemento dalla posizione originale
    newConnections.splice(draggedItem, 1);
    
    // Calcola la nuova posizione di inserimento
    const newDropIndex = draggedItem < dropIndex ? dropIndex - 1 : dropIndex;
    
    // Inserisci l'elemento nella nuova posizione
    newConnections.splice(newDropIndex, 0, draggedConnection);
    
    // Crea il nuovo mapping dell'ordine per tutte le connessioni
    const newOrderMap = {};
    newConnections.forEach((conn, index) => {
      newOrderMap[conn.id] = index;
    });

    // Aggiorna il nodo con il nuovo ordine
    onUpdateConnectionOrder(node.id, newOrderMap);

    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // Gestisce il cambiamento del numero per le connessioni GironeToGirone, GironeToDiretta, e tutte le connessioni da/verso Classifica
  const handleNumberChange = (connectionId, newNumber) => {
    onUpdateConnectionNumber(connectionId, newNumber);
  };

  // Gestisce la cancellazione di una connessione
  const handleDeleteConnection = (connectionId) => {
    if (onDeleteConnection) {
      onDeleteConnection(connectionId);
    }
  };

  if (incomingConnections.length === 0) {
    return (
      <div className="incoming-connections-section">
        <h4>Connessioni in ingresso</h4>
        <p className="no-connections">Nessuna connessione in ingresso</p>
      </div>
    );
  }

  return (
    <div className="incoming-connections-section">
      <h4>Connessioni in ingresso</h4>
      <div className="connections-help">
        {incomingConnections.some(conn => conn.isReorderable) 
          ? "Trascina per riordinare le connessioni GironeToDiretta e ClassificaToDiretta" 
          : "Le connessioni non sono riordinabili per questo tipo di nodo"}
      </div>
      
      <div className="connections-list">
        {incomingConnections.map((connection, index) => (
          <div
            key={connection.id}
            className={`connection-item ${
              draggedItem === index ? 'dragging' : ''
            } ${
              dragOverIndex === index ? 'drag-over' : ''
            } ${
              !connection.isReorderable ? 'non-reorderable' : 'reorderable'
            }`}
            draggable={connection.isReorderable}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <div className="connection-handle">
              {connection.isReorderable ? '⋮⋮' : '●'}
            </div>
            <div className="connection-info">
              <span className="connection-number">
                {index + 1}
              </span>
              <span className="connection-source">
                {connection.sourceNodeName}
              </span>
              
              {/* Campo numero editabile per GironeToGirone, GironeToDiretta, connessioni verso/da Classifica */}
              {(connection.type === 'GironeToGirone' || connection.type === 'GironeToDiretta' || 
                connection.type === 'GironeToClassifica' || connection.type === 'DiretteToClassifica' || 
                connection.type === 'SquadraToClassifica' || connection.type === 'ClassificaToGirone' ||
                connection.type === 'ClassificaToDiretta' || connection.type === 'ClassificaToClassifica') && (
                <div className="connection-controls">
                  <input
                    type="number"
                    className="connection-number-input"
                    value={connection.connectionNumber || ''}
                    onChange={(e) => handleNumberChange(connection.id, parseInt(e.target.value) || 0)}
                    placeholder="N°"
                    min="0"
                    max="999"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    className="delete-connection-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteConnection(connection.id);
                    }}
                    title="Rimuovi connessione"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IncomingConnectionsList;